// Analytics/crash-reporting consent — a tiny out-of-React observable.
//
// The consent flag needs to be readable and subscribable from plain
// modules that run before (or outside) the React tree — notably
// `sentry.ts`, which defers Sentry.init() until consent flips true.
// Keeping it here (rather than in a Zustand store) means `sentry.ts` can
// depend on it without pulling in the store's module graph at boot.
//
// Source of truth is still Rust (`analyticsConsent` in settings.json,
// flipped via `settings.set_analytics_consent`). This module is the
// in-memory mirror the frontend reads/subscribes to; it is hydrated at
// boot by `settingsStore.initSettings` and updated by the Privacy toggle.

let granted = false;
const listeners = new Set<(granted: boolean) => void>();

/** Set the current consent value, notifying subscribers if it changed. */
export function setAnalyticsConsent(next: boolean): void {
  if (next === granted) return;
  granted = next;
  for (const cb of listeners) cb(granted);
}

/** Read the current consent value. Defaults to `false`. */
export function getAnalyticsConsent(): boolean {
  return granted;
}

/**
 * Subscribe to consent changes. Fires immediately with the current value
 * so callers don't miss a flip that happened before they subscribed.
 * Returns an unsubscribe function.
 */
export function onAnalyticsConsent(cb: (granted: boolean) => void): () => void {
  listeners.add(cb);
  cb(granted);
  return () => {
    listeners.delete(cb);
  };
}
